import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { distinctUntilChanged, map, ReplaySubject, Subject, take, takeUntil } from 'rxjs';
import { TableHeaderDirective } from '@app/shared/components/organisms/table/directives/table-header.directive';
import { TableCellDirective } from '@app/shared/components/organisms/table/directives/table-cell.directive';
import { RowCellsDirective } from '@app/shared/components/organisms/table/directives/row-cells.directive';
import { RowHeadersDirective } from '@app/shared/components/organisms/table/directives/row-headers.directive';
import { ColumnHeaderDirective } from '@app/shared/components/organisms/table/directives/column-header.directive';
import { StickyDirective } from '@app/shared/components/organisms/table/directives/sticky.directive';
import { TableDirective } from '@app/shared/components/organisms/table/directives/table.directive';
import { BaseTableComponent } from '@app/shared/components/organisms/table/base-table.component';
import { TableFooterComponent } from '@app/shared/components/organisms/table/table-footer/table-footer.component';
import { Pageable } from '@app/shared/interfaces/pagination.interface';
import { MESSAGES } from '@app/shared/utils/messages';

export interface TableSort {
  field: string;
  direction: 'asc' | 'desc';
}

interface HeaderOption {
  name?: string;
  sort?: 'asc' | 'desc';
  field?: string;
  type?: 'filter' | 'sort';
  lastModifiedDate: number;
  values?: string[];
}

type TemplateBlock = 'loadingBlock' | 'messageBlock' | 'dataBlock';

type GroupByColumn = { [key: string]: StickyDirective[] };

// TODO : modifier le nom de ce composant quand app-base-table / BaseTableComponent sera supprimé
/**
 * Variante de BaseTableComponent où la pagination, le tri et les filtres sont délégués au serveur :
 * le composant affiche la page fournie via [pageable] telle quelle et se contente d'émettre
 * pageableChange / sortChange / filterChange pour que le parent déclenche un nouvel appel.
 * Le provider ci-dessous permet de réutiliser les directives de colonnes et app-column-header
 * qui injectent BaseTableComponent, sans toucher au composant d'origine.
 */
@Component({
  selector: 'app-server-table',
  templateUrl: 'server-table.component.html',
  styleUrls: ['../base-table.component.scss'],
  imports: [CommonModule, TableFooterComponent, NgxSkeletonLoaderModule, TableDirective],
  standalone: true,
  providers: [{ provide: BaseTableComponent, useExisting: forwardRef(() => ServerTableComponent) }],
})
export class ServerTableComponent<T> implements AfterViewInit, OnInit, AfterContentChecked, OnDestroy, OnChanges {
  private unsubscribe$: Subject<boolean> = new Subject<boolean>();

  @Input() isLoading = true;

  @Input() showFooter = true;

  @Input() id = 'default-server-table-id';

  @Input() pageable: Pageable<T> = { content: [], total: 0, limit: 10, offset: 0 };

  @Input() limits?: number[];

  @Input() displayedColumns?: string[];

  @Input() messageInfo: string = '';

  @Input() tableLayoutAuto: boolean = false;

  @Output() pageableChange = new EventEmitter<Pageable<T>>();

  @Output() sortChange = new EventEmitter<TableSort>();

  @Output() filterChange = new EventEmitter<Record<string, string[]>>();

  protected displayedPage: T[] = [];

  protected rows: number[] = [];

  protected headerRows: number[] = Array.from(Array(1).keys());

  @ContentChildren(forwardRef(() => ColumnHeaderDirective))
  private columns!: QueryList<ColumnHeaderDirective>;

  @ContentChildren(TableHeaderDirective)
  protected headers!: QueryList<TableHeaderDirective>;

  @ContentChildren(TableCellDirective)
  protected cells!: QueryList<TableCellDirective<T>>;

  @ContentChildren(StickyDirective)
  private stickyElements!: QueryList<StickyDirective>;

  @ContentChild(RowHeadersDirective)
  private rowsHeaders!: RowHeadersDirective;

  @ContentChild(RowCellsDirective)
  private rowsCells!: RowCellsDirective;

  @ViewChildren('headersVcr', { read: ViewContainerRef })
  private headersVcr!: QueryList<ViewContainerRef>;

  @ViewChildren('rowsVcr', { read: ViewContainerRef })
  private rowsVcr!: QueryList<ViewContainerRef>;

  @ViewChild('tableContainer')
  public readonly tableContainer!: ElementRef;

  private trViewRefs: EmbeddedViewRef<any>[] = [];

  private viewInitialized = false;

  private columnsEvent = new ReplaySubject<QueryList<ColumnHeaderDirective>>(1);

  headerFilters: HeaderOption[] = [];

  protected readonly Array = Array;

  protected limit: number = 10;

  protected currentPage: number = 1;

  private headersInitialized = false;

  private currentSort?: TableSort;

  private currentFiltersKey = '{}';

  private pendingPageable?: Pageable<T>;

  noFilteredDataMessage: { title: string; imgSrc: string } = {
    title: MESSAGES.NO_DATA_ERROR,
    imgSrc: 'assets/no_data.svg',
  };

  constructor(
    private renderer: Renderer2,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  get source(): T[] {
    const content = this.pageable?.content ?? [];
    if (content.length) return content;
    return this.isFiltered ? ([{}] as T[]) : [];
  }

  ngOnInit(): void {
    const [limit] = this.limits || [this.limit];
    this.limit = limit;

    this.columnsEvent
      .pipe(
        takeUntil(this.unsubscribe$),
        map((columnEvent) => this.mapOptions(columnEvent)),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      )
      .subscribe({
        next: (headerOptions: HeaderOption[]) => this.onHeaderOptionsChange(headerOptions),
      });
  }

  private onHeaderOptionsChange(headerOptions: HeaderOption[]): void {
    this.headerFilters = headerOptions;
    this.removePreviousSort();

    const sort = this.extractSort(headerOptions);
    const filters = this.extractFilters(headerOptions);
    const filtersKey = JSON.stringify(filters);

    if (!this.headersInitialized) {
      if (headerOptions.length) this.headersInitialized = true;
      this.currentSort = sort;
      this.currentFiltersKey = filtersKey;
      this.changeDetectorRef.detectChanges();
      return;
    }

    if (sort && (sort.field !== this.currentSort?.field || sort.direction !== this.currentSort?.direction)) {
      this.currentSort = sort;
      Promise.resolve().then(() => this.sortChange.emit(sort));
    }

    if (filtersKey !== this.currentFiltersKey) {
      this.currentFiltersKey = filtersKey;
      Promise.resolve().then(() => this.filterChange.emit(filters));
    }

    this.changeDetectorRef.detectChanges();
  }

  private extractSort(headerOptions: HeaderOption[]): TableSort | undefined {
    const sorts = headerOptions.filter(({ type, sort }) => type === 'sort' && sort !== undefined);
    const latest = sorts.reduce(
      (acc: HeaderOption | undefined, option) =>
        !acc || option.lastModifiedDate >= acc.lastModifiedDate ? option : acc,
      undefined,
    );

    return latest?.field ? { field: latest.field, direction: latest.sort! } : undefined;
  }

  private extractFilters(headerOptions: HeaderOption[]): Record<string, string[]> {
    return headerOptions
      .filter(({ type, field, values }) => type === 'filter' && field && values?.length)
      .reduce(
        (acc, { field, values }) => {
          acc[field!] = values!;
          return acc;
        },
        {} as Record<string, string[]>,
      );
  }

  ngAfterContentChecked(): void {
    const { columnsEvent, stickyElements, columns } = this;

    columnsEvent.next(columns);

    if (stickyElements) this.initStickyElements();
  }

  private initStickyElements() {
    const accPosition: GroupByColumn = {};

    this.stickyElements.reduce((acc, item) => {
      const { columnName } = item;
      if (acc[columnName]) acc[columnName].push(item);
      else acc[columnName] = [item];

      return acc;
    }, accPosition);

    const position = { left: 0, right: 0 };
    const shadowBoxColumns = this.getExtremityColumns(accPosition);

    Object.entries(accPosition).forEach(([, stickyElements]) => {
      const [item] = stickyElements;
      const { offsetWidth } = item.el.nativeElement;

      stickyElements.forEach((directive) => {
        if (directive.isRightDirection()) directive.setRight(position.right);
        else directive.setLeft(position.left);

        if (shadowBoxColumns.includes(directive.columnName)) directive.addBoxShadow();
      });

      if (item.isRightDirection()) position.right += offsetWidth;
      else position.left += offsetWidth;
    });
  }

  private getExtremityColumns(position: GroupByColumn): string[] {
    const groupByDirection: any = { left: [], right: [] };

    Object.entries(position)
      .map(([name, [element]]) => {
        const direction = element.isRightDirection() ? 'right' : 'left';
        return [name, direction];
      })
      .reduce((acc, [name, direction]) => {
        if (acc[direction]) acc[direction].push(name);
        else acc[direction] = [name];

        return acc;
      }, groupByDirection);

    const { left, right } = groupByDirection;
    return [left.pop(), right.shift()];
  }

  private removePreviousSort() {
    const [, ...previousSorts] = this.headerFilters
      .filter(({ type }) => type === 'sort')
      .filter(({ lastModifiedDate }) => lastModifiedDate > 0)
      .reverse();

    const columnNames = previousSorts.map(({ name }) => name);

    this.columns &&
      this.columns
        .filter((column) => column.header !== undefined)
        .filter(({ header }) => columnNames.includes(header!.name))
        .forEach((column) => column.removeSort());
  }

  private mapOptions(columns: QueryList<ColumnHeaderDirective>): HeaderOption[] {
    return columns
      .filter((column) => column.header !== undefined)
      .map(({ header }) => header!)
      .filter(({ field }) => ![field].includes(undefined))
      .sort((a, b) => a.lastModifiedDate - b.lastModifiedDate)
      .map((header) => header.extractOptions());
  }

  get isFiltered() {
    return !!this.headerFilters.filter(({ type, values }) => type === 'filter' && values?.length).length;
  }

  ngAfterViewInit() {
    const { rowsVcr, headersVcr } = this;

    headersVcr.changes.pipe(take(1)).subscribe({
      next: () => this.updateHeaders(),
    });

    rowsVcr.changes.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: () => this.updateRows(),
    });

    this.viewInitialized = true;
    this.renderPage();
  }

  private renderPage() {
    this.cleanTableViews();

    this.displayedPage = this.pageable?.content ?? [];
    this.rows = Array.from(Array(this.displayedPage.length).keys());

    this.headersVcr?.notifyOnChanges();
    this.rowsVcr?.notifyOnChanges();

    this.changeDetectorRef.detectChanges();
  }

  private updateHeaders() {
    const { headersVcr, headers, rowsHeaders } = this;

    if (!rowsHeaders) return;

    headersVcr!.forEach((vcr) => {
      const trView = vcr.createEmbeddedView(rowsHeaders.templateRef);
      const [tr] = trView.rootNodes;

      headers.forEach((directive) => {
        const thView = vcr!.createEmbeddedView(directive.templateRef);
        const [th] = thView.rootNodes;

        this.renderer.appendChild(tr, th);
      });
    });
  }

  private updateRows() {
    if (!this.rowsCells || !this.rowsVcr) return;

    this.cleanTableViews();

    this.rowsVcr!.forEach((vcr, index) => {
      const $implicit = this.displayedPage[index];
      if (!$implicit) return;

      const context = { $implicit, index };
      const trView = vcr.createEmbeddedView(this.rowsCells.templateRef, context);
      this.trViewRefs.push(trView);
      const [tr] = trView.rootNodes;

      this.cells.forEach((directive) => {
        const tdView = vcr.createEmbeddedView(directive.templateRef, context);
        const [td] = tdView.rootNodes;
        this.renderer.appendChild(tr, td);
      });
    });
  }

  private cleanTableViews() {
    this.trViewRefs.forEach((trViewRef) => trViewRef.destroy());
    this.trViewRefs = [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pageable && this.pageable) {
      this.limit = this.pageable.limit || this.limit;
      this.currentPage = Math.floor(this.pageable.offset / this.pageable.limit) + 1;
      if (this.viewInitialized) {
        this.renderPage();
      }
    }
  }

  protected get currentTemplate(): TemplateBlock {
    const { isLoading, displayedPage } = this;
    if (isLoading) return 'loadingBlock';
    if (!displayedPage.length) return 'messageBlock';
    return 'dataBlock';
  }

  get displayableRows() {
    return this.pageable?.content ?? [];
  }

  onLimitChange(limit: number) {
    const reference = this.pendingPageable ?? this.pageable;
    this.schedulePageableEmit({ ...reference, limit, offset: 0 });
  }

  onPageChange(currentPage: number) {
    const reference = this.pendingPageable ?? this.pageable;
    const offset = (currentPage - 1) * this.pageable.limit;
    this.schedulePageableEmit({ ...reference, offset });
  }

  /**
   * Le footer émet un changement de page puis un changement de limite
   */
  private schedulePageableEmit(next: Pageable<T>) {
    const isFirst = !this.pendingPageable;
    this.pendingPageable = next;

    if (!isFirst) return;

    Promise.resolve().then(() => {
      const pending = this.pendingPageable;
      this.pendingPageable = undefined;

      if (!pending) return;
      if (pending.offset === this.pageable.offset && pending.limit === this.pageable.limit) return;

      this.pageableChange.emit(pending);
    });
  }

  ngOnDestroy(): void {
    this.columnsEvent.complete();
    this.unsubscribe$.next(true);
  }
}
